import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
} from "@chakra-ui/react";
import LanguageConfiguration from "./LanguageConfiguration.tsx";
import LanguageInput from "./LanguageInput.tsx";
import type {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import LanguageDefaultConfiguration from "../LanguageDefaultConfigurationIcon.tsx";

interface StepTargetLanguagesProps {
  availableLanguages: string[];
  selectedSourceLanguage: string;
  selectedTargets: Record<string, ILanguageConfiguration>;
  onAddLanguage: (lang: string) => void;
  onRemoveLanguage: (lang: string) => void;
  setTargetConfig: (lang: string, config: Partial<ILanguageConfiguration>) => void;
}

const StepTargetLanguages: React.FC<StepTargetLanguagesProps> = (
  {
    availableLanguages,
    selectedSourceLanguage,
    selectedTargets,
    onAddLanguage,
    onRemoveLanguage,
    setTargetConfig,
  }: StepTargetLanguagesProps) => {
  const [isInputOpen, setIsInputOpen] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState<string>("");
  const selectedLanguageNames: string[] = Object.keys(selectedTargets);
  const remainingLanguages: string[] = availableLanguages.filter(
    (language: string) => !selectedLanguageNames.includes(language) && language !== selectedSourceLanguage
  );

  return (
    <div className="flex w-full flex-col gap-2">
      <LanguageInput
        isOpen={isInputOpen}
        setIsOpen={setIsInputOpen}
        languages={remainingLanguages}
        selectedLanguage={inputValue}
        onSelectLanguage={(language: string) => {
          onAddLanguage(language);
          setInputValue("");
        }}
        onChange={(value: string) => setInputValue(value)}
        placeholder="Select target language"
      />

      <Accordion allowToggle className="overflow-y-auto">
        {selectedLanguageNames.map((language: string) => (
          <Box key={language} my={4}>
            <AccordionItem border="none" borderBottom="1px solid gray">
              <h2>
                <AccordionButton color="white">
                  <Box flex="1" textAlign="left">{language}</Box>
                  <AccordionIcon/>
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Flex className="relative" direction="column" gap={2}>
                  <LanguageConfiguration
                    config={selectedTargets[language]}
                    setConfig={(update: Partial<ILanguageConfiguration>) => setTargetConfig(language, update)}
                    action={
                      <LanguageDefaultConfiguration
                        lang={language}
                        currentConfig={selectedTargets[language]}
                        updateConfig={(config: Partial<ILanguageConfiguration>) => setTargetConfig(language, config)}
                      />
                    }
                  />
                  <Button
                    type="button"
                    size="sm"
                    colorScheme="red"
                    onClick={() => onRemoveLanguage(language)}
                  >
                    Remove
                  </Button>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          </Box>
        ))}
      </Accordion>
    </div>
  );
};

export default StepTargetLanguages;
